import React, { useState } from 'react';
import { Siswa, TagihanSiswa, TransaksiPembayaran } from '../../types/index.js';
import {
  Smartphone,
  CreditCard,
  Wallet,
  Copy,
  QrCode,
  Bell,
  CheckCircle2,
  Clock,
  ChevronRight,
  ShieldCheck,
  FileText,
  User,
  Home,
  Receipt,
  Sparkles,
  RefreshCw,
  Download,
  ArrowUpRight,
  AlertCircle,
  ArrowRightLeft,
} from 'lucide-react';
import { useDialog } from '../ui/DialogProvider.js';
import { formatPaymentMethod, getPokokNominal } from '../../utils/paymentMethod.js';
import { api } from '../../services/api.js';

interface ExpoMobileSimulatorProps {
  siswaList: Siswa[];
  tagihanList: TagihanSiswa[];
  transaksiList: TransaksiPembayaran[];
  onProcessPayment: (payload: any) => Promise<any>;
  isRealUser?: boolean;
}

export const ExpoMobileSimulator: React.FC<ExpoMobileSimulatorProps> = ({
  siswaList,
  tagihanList,
  transaksiList,
  onProcessPayment,
  isRealUser = false,
}) => {
  const { showAlert } = useDialog();
  const [activeTab, setActiveTab] = useState<'HOME' | 'TAGIHAN' | 'TABUNGAN' | 'AKUN'>('HOME');
  const defaultSiswaId = siswaList.length > 0 ? siswaList[0].id : '';
  const [selectedStudentId, setSelectedStudentId] = useState(defaultSiswaId);
  const [quickPin, setQuickPin] = useState('');
  const [copiedVA, setCopiedVA] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [showEWalletQr, setShowEWalletQr] = useState(false);
  const [qrisPaymentData, setQrisPaymentData] = useState<any>(null);
  const [selectedBills, setSelectedBills] = useState<string[]>([]);
  
  const [targetSiswaId, setTargetSiswaId] = useState(defaultSiswaId);
  const [activeUser, setActiveUser] = useState<Siswa | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<'QRIS' | 'DUITKU_VA' | 'DUITKU_QRIS' | 'MIDTRANS_SNAP' | 'TABUNGAN' | 'EWALLET'>('QRIS');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccessNotice, setPaymentSuccessNotice] = useState<string | null>(null);
  const [showTopUpTabunganModal, setShowTopUpTabunganModal] = useState(false);
  const [topUpNominal, setTopUpNominal] = useState(50000);
  const [topUpSource, setTopUpSource] = useState<'EWALLET' | 'DUITKU_VA' | 'DUITKU_QRIS'>('EWALLET');
  const [isToppingUp, setIsToppingUp] = useState(false);

  const student = siswaList.find((s) => s.id === selectedStudentId) || siswaList[0];
  const unpaidBills = tagihanList.filter((t) => t.siswaId === student?.id && t.status !== 'PAID');
  const totalUnpaid = unpaidBills.reduce((sum, b) => sum + b.sisa, 0);
  const studentTransactions = transaksiList.filter((t) => t.siswaId === student?.id);

  const handleDownloadQR = async () => {
    const qrUrl = qrisPaymentData?.qrisUrl || `https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${encodeURIComponent(qrisPaymentData?.qrisString || '')}`;
    const filename = `QRIS-${qrisPaymentData?.noTransaksi || 'Payment'}.png`;

    try {
      // First attempt: direct fetch (works if backend/API has CORS enabled)
      const response = await fetch(qrUrl);
      if (!response.ok) throw new Error('Network error');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Second attempt: use allorigins proxy for cross-origin images
      try {
        const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(qrUrl)}`;
        const response = await fetch(proxyUrl);
        if (!response.ok) throw new Error('Proxy error');
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (proxyError) {
        // Ultimate fallback: open in new tab
        window.open(qrUrl, '_blank');
      }
    }
  };

  const handleCopyVA = () => {
    if (student?.noVA) {
      navigator.clipboard.writeText(student.noVA);
      setCopiedVA(true);
      setTimeout(() => setCopiedVA(false), 2000);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedBills((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleMobilePay = async () => {
    if (selectedBills.length === 0) {
      showAlert('Pilih minimal satu tagihan untuk dibayar.', { type: 'warning' });
      return;
    }

    try {
      setIsProcessing(true);
      const items = selectedBills.map((id) => {
        const b = tagihanList.find((x) => x.id === id);
        return { tagihanId: id, nominal: b?.sisa || 0 };
      });
      const totalBayar = items.reduce((s, i) => s + i.nominal, 0);

      if (selectedChannel === 'EWALLET') {
        if ((student.saldoEWallet || 0) < totalBayar) {
          showAlert(`Saldo E-Wallet siswa (Rp ${(student.saldoEWallet || 0).toLocaleString('id-ID')}) tidak mencukupi untuk pembayaran sebesar Rp ${totalBayar.toLocaleString('id-ID')}!`, { type: 'danger' });
          return;
        }
      }

      if (selectedChannel === 'TABUNGAN') {
        if ((student.saldoTabungan || 0) < totalBayar) {
          showAlert(`Saldo Tabungan siswa (Rp ${(student.saldoTabungan || 0).toLocaleString('id-ID')}) tidak mencukupi untuk pembayaran sebesar Rp ${totalBayar.toLocaleString('id-ID')}!`, { type: 'danger' });
          return;
        }
      }

      const mappedChannel = selectedChannel === 'QRIS' ? 'DUITKU_QRIS' : selectedChannel;

      const res = await onProcessPayment({
        siswaId: student.id,
        items,
        tagihanIds: selectedBills,
        totalBayar,
        metodeBayar: mappedChannel,
        channel: mappedChannel,
        kasir: 'Portal Siswa',
        keterangan: `Pembayaran Aplikasi Mobile Portal (${mappedChannel})`,
      });

      const trx = res?.transaction;

      // Jika pembayaran QRIS/Gateway: tampilkan QR Code modal
      if (trx?.qrisString || trx?.qrisUrl || trx?.paymentUrl) {
        setQrisPaymentData(trx);
        setShowQrModal(true);
        setSelectedBills([]);
      } else {
        // Pembayaran langsung (Cash/Tabungan/E-Wallet): tampilkan notifikasi sukses
        if (selectedChannel === 'EWALLET') {
          student.saldoEWallet = Math.max(0, (student.saldoEWallet || 0) - totalBayar);
        } else if (selectedChannel === 'TABUNGAN') {
          student.saldoTabungan = Math.max(0, (student.saldoTabungan || 0) - totalBayar);
        }
        setSelectedBills([]);
        const nominalText = (trx?.totalBayar || totalBayar).toLocaleString('id-ID');
        setPaymentSuccessNotice(`Pembayaran Rp ${nominalText} berhasil diverifikasi! Saldo otomatis didebit.`);
        setTimeout(() => setPaymentSuccessNotice(null), 5000);
      }
    } catch (err: any) {
      showAlert(`Gagal: ${err.message}`, { type: 'danger' });
    } finally {
      setIsProcessing(false);
    }
  };

  if (!student) {
    return (
      <div className="flex items-center justify-center p-8 bg-slate-50 rounded-xl border border-slate-200">
        <p className="text-slate-500 font-medium">Memuat data siswa...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      {/* Simulator Control Banner */}
      {!isRealUser && (
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-amber-500" />
              <h2 className="text-base font-bold text-slate-900">
                Expo React Native Mobile App Simulator (Android / iOS)
              </h2>
            </div>
            <p className="text-xs text-slate-500">
              Aplikasi mobile wali murid terintegrasi real-time API, Duitku payment gateway, dan push notifications
            </p>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold text-slate-600">Simulasikan Siswa:</span>
            <select
              value={selectedStudentId}
              onChange={(e) => {
                setSelectedStudentId(e.target.value);
                setSelectedBills([]);
              }}
              className="text-xs border border-slate-300 rounded-md px-3 py-1.5 bg-slate-50 font-medium"
            >
              {siswaList.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nama} ({s.kelasNama})
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Center Phone Device Wrapper */}
      <div className="flex justify-center py-4">
        {/* Smartphone Hardware Frame */}
        <div className="w-[380px] h-[720px] bg-slate-950 rounded-[44px] p-3 shadow-2xl border-[4px] border-slate-800 relative flex flex-col overflow-hidden">
          {/* Top Speaker & Dynamic Island */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 w-32 h-4 bg-slate-900 rounded-full z-50 flex items-center justify-center">
            <div className="w-3 h-3 rounded-full bg-slate-950 border border-slate-800"></div>
          </div>

          {/* Screen Canvas */}
          <div className="w-full h-full bg-slate-50 rounded-[34px] overflow-hidden flex flex-col relative text-slate-800 select-none">
            {/* Mobile Status Bar */}
            <div className="bg-sky-700 text-white px-6 pt-3 pb-2 flex items-center justify-between text-[11px] font-semibold">
              <span>09:41</span>
              <div className="flex items-center gap-1.5 text-[10px]">
                <span>5G</span>
                <div className="w-4 h-2 border border-white rounded-xs p-0.5 flex items-center">
                  <div className="w-2.5 h-1 bg-white rounded-xs"></div>
                </div>
              </div>
            </div>

            {/* Mobile App Header */}
            <div className="bg-sky-700 text-white px-4 pb-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center font-bold text-amber-300 text-xs">
                    SDMT
                  </div>
                  <div>
                    <h3 className="text-xs font-bold leading-tight">SDMT Mobile</h3>
                    <p className="text-[10px] text-sky-200">SD Muhammadiyah Terpadu</p>
                  </div>
                </div>

                <button className="relative p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition">
                  <Bell className="w-4 h-4 text-white" />
                  {unpaidBills.length > 0 && (
                    <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-400"></span>
                  )}
                </button>
              </div>

              {/* Student Identification Pill */}
              <div className="mt-3 bg-white/10 backdrop-blur-sm rounded-xl p-2.5 border border-white/15 flex items-center justify-between text-xs">
                <div>
                  <p className="font-bold text-white text-xs">{student.nama}</p>
                  <p className="text-[10px] text-sky-200">
                    NIS: {student.nis} • Kelas: {student.kelasNama}
                  </p>
                </div>
                <span className="bg-amber-400 text-slate-950 font-extrabold text-[10px] px-2 py-0.5 rounded">
                  AKTIF
                </span>
              </div>
            </div>

            {/* Success Toast / Notification */}
            {paymentSuccessNotice && (
              <div className="bg-emerald-600 text-white text-xs px-3 py-2 text-center font-semibold flex items-center justify-center gap-1.5 animate-in slide-in-from-top">
                <CheckCircle2 className="w-4 h-4 text-emerald-200" />
                <span>{paymentSuccessNotice}</span>
              </div>
            )}

            {/* Scrollable Screen Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3.5 text-xs">
              {/* TAB 1: HOME */}
              {activeTab === 'HOME' && (
                <>
                  {/* Virtual Account Card */}
                  <div className="bg-gradient-to-r from-sky-900 to-indigo-900 text-white p-3.5 rounded-2xl shadow-sm space-y-2 border border-sky-800">
                    <div className="flex justify-between items-center text-[10px] text-sky-300">
                      <span>VIRTUAL ACCOUNT RESMI (BMI / DUITKU)</span>
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="font-mono text-base font-extrabold tracking-wider text-amber-300">
                        {student.noVA}
                      </span>
                      <button
                        onClick={handleCopyVA}
                        className="bg-white/20 hover:bg-white/30 text-white text-[10px] px-2 py-1 rounded-md flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" />
                        <span>{copiedVA ? 'Tersalin' : 'Salin'}</span>
                      </button>
                    </div>

                    <div className="flex justify-between items-center text-[10px] text-sky-200 border-t border-white/10 pt-2 pb-1">
                      <span>Saldo Tabungan:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-emerald-300 text-xs">
                          Rp {(student.saldoTabungan || 0).toLocaleString('id-ID')}
                        </span>
                        <button
                          onClick={() => {
                            setTopUpSource('EWALLET');
                            setShowTopUpTabunganModal(true);
                          }}
                          className="bg-emerald-500/80 hover:bg-emerald-400 text-white text-[9px] font-bold px-2 py-0.5 rounded shadow-xs cursor-pointer transition"
                          title="Top Up Tabungan dari Saldo E-Wallet"
                        >
                          + Top Up
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-[10px] text-sky-200 border-t border-white/10 pt-1">
                      <span>Saldo E-Wallet:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-indigo-300 text-xs">
                          Rp {(student.saldoEWallet || 0).toLocaleString('id-ID')}
                        </span>
                        <button
                          onClick={() => {
                            setSelectedChannel('EWALLET');
                            setActiveTab('TAGIHAN');
                          }}
                          className="bg-indigo-500/80 hover:bg-indigo-400 text-white text-[9px] font-bold px-2 py-0.5 rounded shadow-xs cursor-pointer transition"
                          title="Gunakan E-Wallet untuk Bayar Tagihan"
                        >
                          Bayar
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Ringkasan Tunggakan Alert */}
                  <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-xs flex items-center justify-between">
                    <div>
                      <span className="text-[10px] text-slate-500 uppercase font-bold block">
                        Total Tagihan Belum Dibayar
                      </span>
                      <span className="font-mono text-base font-black text-rose-600">
                        Rp {totalUnpaid.toLocaleString('id-ID')}
                      </span>
                      <p className="text-[10px] text-slate-400 mt-0.5">{unpaidBills.length} pos tagihan aktif</p>
                    </div>

                    <button
                      onClick={() => setActiveTab('TAGIHAN')}
                      className="bg-sky-600 hover:bg-sky-500 text-white text-[11px] font-bold px-3 py-1.5 rounded-lg shadow-xs cursor-pointer transition"
                    >
                      Bayar
                    </button>
                  </div>

                  {/* Menu Shortcuts */}
                  <div className="grid grid-cols-4 gap-2 text-center text-[10px]">
                    <button
                      onClick={() => {
                        setSelectedChannel('EWALLET');
                        setActiveTab('TAGIHAN');
                      }}
                      className="bg-white p-2.5 rounded-xl border border-slate-200 flex flex-col items-center gap-1 hover:border-sky-300 transition shadow-2xs cursor-pointer"
                    >
                      <CreditCard className="w-5 h-5 text-sky-600" />
                      <span className="font-semibold">Bayar Tagihan</span>
                    </button>

                    <button
                      onClick={() => setActiveTab('TABUNGAN')}
                      className="bg-white p-2.5 rounded-xl border border-slate-200 flex flex-col items-center gap-1 hover:border-emerald-300 transition shadow-2xs cursor-pointer"
                    >
                      <Wallet className="w-5 h-5 text-emerald-600" />
                      <span className="font-semibold">Tabungan</span>
                    </button>

                    <button
                      onClick={() => {
                        setTopUpSource('EWALLET');
                        setShowTopUpTabunganModal(true);
                      }}
                      className="bg-white p-2.5 rounded-xl border border-slate-200 flex flex-col items-center gap-1 hover:border-indigo-300 transition shadow-2xs cursor-pointer"
                    >
                      <ArrowUpRight className="w-5 h-5 text-indigo-600" />
                      <span className="font-semibold">Top Up Tabungan</span>
                    </button>

                    <button
                      onClick={() => setShowEWalletQr(true)}
                      className="bg-white p-2.5 rounded-xl border border-slate-200 flex flex-col items-center gap-1 hover:border-purple-300 transition shadow-2xs cursor-pointer"
                    >
                      <Smartphone className="w-5 h-5 text-purple-600" />
                      <span className="font-semibold">QR E-Wallet</span>
                    </button>
                  </div>

                  {/* Recent Transactions in App */}
                  <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-xs space-y-2">
                    <div className="flex justify-between items-center pb-1.5 border-b border-slate-100">
                      <span className="font-bold text-xs text-slate-800">Riwayat Pembayaran Terakhir</span>
                      <span className="text-[10px] text-sky-600 font-semibold">Lihat Semua</span>
                    </div>

                    {studentTransactions.slice(0, 3).map((trx) => (
                      <div key={trx.id} className="flex justify-between items-center py-1.5 border-b border-slate-50">
                        <div>
                          <p className="font-semibold text-slate-900 text-[11px]">{formatPaymentMethod(trx.channel, trx.metodeBayar)}</p>
                          <p className="text-[10px] text-slate-400">{trx.tanggal.slice(0, 10)}</p>
                        </div>
                        <span className="font-mono font-bold text-emerald-700 text-xs">
                          Rp {getPokokNominal(trx).toLocaleString('id-ID')}
                        </span>
                      </div>
                    ))}
                    {studentTransactions.length === 0 && (
                      <p className="text-center py-3 text-slate-400 text-[11px]">Belum ada riwayat transaksi</p>
                    )}
                  </div>
                </>
              )}

              {/* TAB 2: TAGIHAN */}
              {activeTab === 'TAGIHAN' && (
                <div className="space-y-3">
                  <div className="bg-white p-3 rounded-xl border border-slate-200">
                    <span className="font-bold text-xs text-slate-900 block mb-2">
                      Pilih Tagihan Yang Ingin Dibayar:
                    </span>

                    <div className="space-y-2">
                      {unpaidBills.map((b) => {
                        const isSelected = selectedBills.includes(b.id);
                        return (
                          <div
                            key={b.id}
                            onClick={() => toggleSelect(b.id)}
                            className={`p-2.5 rounded-lg border cursor-pointer flex items-center justify-between transition ${
                              isSelected ? 'bg-sky-50 border-sky-400' : 'bg-slate-50 border-slate-200'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                readOnly
                                className="rounded text-sky-600"
                              />
                              <div>
                                <p className="font-bold text-slate-900 text-xs">{b.namaPos}</p>
                                <p className="text-[10px] text-slate-500">
                                  {b.bulan || b.tahunAjaran}
                                </p>
                              </div>
                            </div>
                            <span className="font-mono font-bold text-rose-600 text-xs">
                              Rp {b.sisa.toLocaleString('id-ID')}
                            </span>
                          </div>
                        );
                      })}
                      {unpaidBills.length === 0 && (
                        <p className="text-center py-6 text-emerald-600 font-bold">
                          Alhamdulillah, semua tagihan telah lunas!
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Payment Gateway Picker */}
                  {selectedBills.length > 0 && (() => {
                    const totalSelectedBills = selectedBills.reduce((s, id) => s + (tagihanList.find(x => x.id === id)?.sisa || 0), 0);
                    return (
                    <div className="bg-white p-3 rounded-xl border border-slate-200 space-y-2">
                      <span className="font-bold text-xs text-slate-900 block">Pilih Metode Pembayaran:</span>

                      {/* QRIS hanya tampil jika total <= 50.000 */}
                      {totalSelectedBills <= 50000 && (
                        <label className="flex items-center gap-2 p-2 border border-rose-200 rounded-lg cursor-pointer bg-rose-50 text-[11px] font-medium text-rose-950">
                          <input
                            type="radio"
                            name="mobileChannel"
                            checked={selectedChannel === 'QRIS' || selectedChannel === 'DUITKU_QRIS'}
                            onChange={() => setSelectedChannel('QRIS')}
                          />
                          <div className="bg-rose-600 text-white font-black text-[8px] px-1 py-0.5 rounded">
                            QRIS
                          </div>
                          <div className="flex-1">
                            <span className="font-bold block">QRIS Standar Nasional</span>
                            <span className="text-[10px] text-rose-700">Maks. Rp 50.000 • Biaya: Rp 1.500</span>
                          </div>
                        </label>
                      )}

                      <label className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer bg-slate-50 text-[11px]">
                        <input
                          type="radio"
                          name="mobileChannel"
                          checked={selectedChannel === 'DUITKU_VA'}
                          onChange={() => setSelectedChannel('DUITKU_VA')}
                        />
                        <span>Virtual Account Bank (BCA, BRI, Mandiri)</span>
                      </label>

                      <label className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer bg-slate-50 text-[11px]">
                        <input
                          type="radio"
                          name="mobileChannel"
                          checked={selectedChannel === 'TABUNGAN'}
                          onChange={() => setSelectedChannel('TABUNGAN')}
                        />
                        <div className="flex-1">
                          <span className="font-bold block">Potong Saldo Tabungan Siswa</span>
                          <span className={`text-[10px] ${
                            (student.saldoTabungan || 0) < totalSelectedBills ? 'text-rose-600 font-semibold' : 'text-slate-500'
                          }`}>
                            Saldo: Rp {(student.saldoTabungan || 0).toLocaleString('id-ID')}
                            {(student.saldoTabungan || 0) < totalSelectedBills && ' ⚠ Tidak Cukup'}
                          </span>
                        </div>
                      </label>

                      <label className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer bg-slate-50 text-[11px]">
                        <input
                          type="radio"
                          name="mobileChannel"
                          checked={selectedChannel === 'EWALLET'}
                          onChange={() => setSelectedChannel('EWALLET')}
                        />
                        <div className="flex-1">
                          <span className="font-bold block">Potong Saldo E-Wallet Siswa</span>
                          <span className={`text-[10px] ${
                            (student.saldoEWallet || 0) < totalSelectedBills ? 'text-rose-600 font-semibold' : 'text-indigo-600 font-semibold'
                          }`}>
                            Saldo: Rp {(student.saldoEWallet || 0).toLocaleString('id-ID')}
                            {(student.saldoEWallet || 0) < totalSelectedBills && ' ⚠ Tidak Cukup'}
                          </span>
                        </div>
                      </label>

                      <button
                        disabled={isProcessing}
                        onClick={handleMobilePay}
                        className="w-full mt-2 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 shadow-xs"
                      >
                        <CreditCard className="w-3.5 h-3.5" />
                        <span>{isProcessing ? 'Memproses...' : `Bayar Sekarang (Rp ${(selectedChannel === 'QRIS' || selectedChannel === 'DUITKU_QRIS' ? totalSelectedBills + 1500 : totalSelectedBills).toLocaleString('id-ID')})`}</span>
                      </button>
                    </div>
                  );
                  })()}
                </div>
              )}

              {/* TAB 3: TABUNGAN */}
              {activeTab === 'TABUNGAN' && (
                <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs space-y-4">
                  <div className="text-center py-2">
                    <p className="text-[10px] text-slate-500 uppercase font-semibold">Total Saldo Tabungan</p>
                    <p className="text-2xl font-mono font-black text-emerald-600 mt-1">
                      Rp {(student.saldoTabungan || 0).toLocaleString('id-ID')}
                    </p>
                    <span className="text-[10px] bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded font-medium inline-block mt-1">
                      Terverifikasi Bank Muamalat SDMT
                    </span>
                  </div>

                  {/* Saldo E-Wallet Card */}
                  <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-xl flex items-center justify-between">
                    <div>
                      <p className="text-[10px] text-indigo-700 font-medium">Saldo E-Wallet Siswa</p>
                      <p className="text-sm font-mono font-bold text-indigo-950">
                        Rp {(student.saldoEWallet || 0).toLocaleString('id-ID')}
                      </p>
                    </div>
                    <span className="text-[9px] bg-indigo-100 text-indigo-800 px-2 py-1 rounded font-bold">
                      Siap Dipakai
                    </span>
                  </div>

                  <button
                    onClick={() => setShowTopUpTabunganModal(true)}
                    className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 shadow-sm transition"
                  >
                    <Wallet className="w-4 h-4" />
                    <span>Isi Saldo / Top Up Tabungan</span>
                  </button>

                  <p className="text-[11px] text-slate-500 leading-relaxed text-center">
                    Saldo tabungan siswa dapat digunakan secara otomatis untuk melunasi tagihan SPP dan administrasi sekolah melalui aplikasi ini.
                  </p>
                </div>
              )}

              {/* TAB 4: AKUN */}
              {activeTab === 'AKUN' && (
                <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs space-y-3 text-xs">
                  <div className="flex items-center gap-3 border-b pb-3">
                    <div className="w-10 h-10 rounded-full bg-sky-100 text-sky-800 font-bold flex items-center justify-center text-sm">
                      {student.nama.charAt(0)}
                    </div>
                    <div>
                      <p className="font-bold text-slate-900">{student.nama}</p>
                      <p className="text-slate-500 text-[10px]">Wali: {student.namaWali || 'Budi Santoso'}</p>
                    </div>
                  </div>

                  <div className="space-y-1 text-slate-600">
                    <p>No. Handphone: <span className="font-mono font-semibold text-slate-800">{student.mobile}</span></p>
                    <p>Alamat Unit: <span className="font-semibold text-slate-800">Siman, Ponorogo</span></p>
                    <p>Versi Mobile: <span className="font-mono text-slate-800">v2.4.0 (Expo SDK 52)</span></p>
                  </div>
                </div>
              )}
            </div>

            {/* Bottom Tab Bar (Matching Expo React Native Navigation) */}
            <div className="bg-white border-t border-slate-200 px-4 py-2 flex items-center justify-around text-[10px] text-slate-500">
              <button
                onClick={() => setActiveTab('HOME')}
                className={`flex flex-col items-center gap-0.5 ${
                  activeTab === 'HOME' ? 'text-sky-700 font-bold' : ''
                }`}
              >
                <Home className="w-4 h-4" />
                <span>Beranda</span>
              </button>

              <button
                onClick={() => setActiveTab('TAGIHAN')}
                className={`flex flex-col items-center gap-0.5 relative ${
                  activeTab === 'TAGIHAN' ? 'text-sky-700 font-bold' : ''
                }`}
              >
                <CreditCard className="w-4 h-4" />
                <span>Tagihan</span>
                {unpaidBills.length > 0 && (
                  <span className="absolute -top-1 right-2 w-2 h-2 rounded-full bg-rose-500"></span>
                )}
              </button>

              <button
                onClick={() => setActiveTab('TABUNGAN')}
                className={`flex flex-col items-center gap-0.5 ${
                  activeTab === 'TABUNGAN' ? 'text-sky-700 font-bold' : ''
                }`}
              >
                <Wallet className="w-4 h-4" />
                <span>Tabungan</span>
              </button>

              <button
                onClick={() => setActiveTab('AKUN')}
                className={`flex flex-col items-center gap-0.5 ${
                  activeTab === 'AKUN' ? 'text-sky-700 font-bold' : ''
                }`}
              >
                <User className="w-4 h-4" />
                <span>Akun</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* E-Wallet QR Modal for Canteen */}
      {showEWalletQr && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-white rounded-2xl p-6 text-center max-w-xs w-full shadow-2xl border border-slate-200">
            <h3 className="font-extrabold text-lg text-slate-800 mb-1">E-Wallet SDMT</h3>
            <p className="text-xs text-slate-500 mb-4">Tunjukkan QR ini ke kasir kantin/koperasi</p>

            <div className="bg-white border-2 border-indigo-100 p-3 rounded-xl inline-block mx-auto mb-4">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=SDMT-EWALLET-${student.nis}`}
                alt="QR E-Wallet"
                className="w-40 h-40 object-contain mx-auto"
              />
            </div>

            <p className="font-bold text-slate-900">{student.nama}</p>
            <p className="text-xs text-slate-500 mb-4">NIS: {student.nis}</p>

            <div className="bg-indigo-50 border border-indigo-100 p-3 rounded-xl mb-4">
              <p className="text-xs text-indigo-800 font-semibold mb-1">Saldo E-Wallet Anda</p>
              <p className="text-xl font-black text-indigo-600">
                Rp {(student.saldoEWallet || 0).toLocaleString('id-ID')}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-3">
              <button
                onClick={() => {
                  setShowEWalletQr(false);
                  setSelectedChannel('EWALLET');
                  setActiveTab('TAGIHAN');
                }}
                className="py-2.5 px-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold shadow-xs transition flex items-center justify-center gap-1 cursor-pointer"
              >
                <CreditCard className="w-3.5 h-3.5" />
                <span>Bayar Tagihan</span>
              </button>
              <button
                onClick={() => {
                  setShowEWalletQr(false);
                  setTopUpSource('EWALLET');
                  setShowTopUpTabunganModal(true);
                }}
                className="py-2.5 px-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold shadow-xs transition flex items-center justify-center gap-1 cursor-pointer"
              >
                <Wallet className="w-3.5 h-3.5" />
                <span>Top Up Tabungan</span>
              </button>
            </div>

            <button
              onClick={() => setShowEWalletQr(false)}
              className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold shadow-sm transition cursor-pointer"
            >
              Tutup
            </button>
          </div>
        </div>
      )}

      {/* QRIS Payment Modal — Dynamic dari transaksi aktual */}
      {showQrModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-white rounded-2xl p-5 text-center max-w-xs w-full shadow-2xl border border-slate-200 max-h-[90vh] overflow-y-auto">
            {/* National QRIS Header */}
            <div className="bg-rose-700 text-white rounded-lg p-2 flex items-center justify-between shadow-xs mb-3">
              <div className="flex items-center gap-1.5">
                <span className="bg-white text-rose-700 font-black px-1.5 py-0.5 rounded text-[10px]">QRIS</span>
                <span className="font-extrabold text-[10px]">STANDAR NASIONAL</span>
              </div>
              <span className="text-[9px] bg-white/20 px-1 py-0.5 rounded font-bold">GPN</span>
            </div>

            <p className="font-bold text-xs text-slate-900 uppercase">
              {qrisPaymentData?.qrisMerchantName || 'SD MUHAMMADIYAH TERPADU'}
            </p>
            <p className="text-[10px] text-slate-500 font-mono">
              NMID: {qrisPaymentData?.qrisNmid || 'ID1020038491028'}
            </p>
            <p className="text-[11px] text-sky-800 font-semibold mt-1">{student.nama} ({student.nis})</p>

            {/* Rincian Nominal & Biaya Layanan QRIS */}
            {qrisPaymentData && (() => {
              const rawTotal = Number(qrisPaymentData.totalBayar || 0);
              const fee = Number(qrisPaymentData.biayaLayanan || 1500);
              const totalToPay = Number(qrisPaymentData.totalBayarGateway || (rawTotal > 0 && rawTotal % 1000 === 500 ? rawTotal : rawTotal + fee));
              const nominalTagihan = totalToPay > fee ? (totalToPay - fee) : rawTotal;
              return (
                <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-200 my-2 text-left space-y-1 text-xs">
                  <div className="flex justify-between text-slate-600 text-[11px]">
                    <span>Nominal Tagihan:</span>
                    <span className="font-mono font-semibold">Rp {nominalTagihan.toLocaleString('id-ID')}</span>
                  </div>
                  <div className="flex justify-between text-sky-700 text-[11px] font-semibold">
                    <span>Biaya Layanan (QRIS):</span>
                    <span className="font-mono font-bold">+Rp {fee.toLocaleString('id-ID')}</span>
                  </div>
                  <div className="border-t border-slate-200 pt-1 flex justify-between items-center text-xs">
                    <span className="font-bold text-slate-800">Total Pembayaran:</span>
                    <span className="font-mono font-black text-rose-600 text-sm">
                      Rp {totalToPay.toLocaleString('id-ID')}
                    </span>
                  </div>
                </div>
              );
            })()}

            {/* Dynamic QR Code dari Backend */}
            <div className="bg-white border border-slate-200 p-3 rounded-xl flex flex-col items-center justify-center my-3 relative">
              {qrisPaymentData?.qrisUrl || qrisPaymentData?.qrisString ? (
                <img
                  src={
                    qrisPaymentData.qrisUrl ||
                    `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(qrisPaymentData.qrisString)}`
                  }
                  alt="QRIS Pembayaran"
                  className="w-44 h-44 object-contain"
                  onError={(e) => {
                    if (qrisPaymentData?.qrisString) {
                      (e.target as HTMLImageElement).src = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(qrisPaymentData.qrisString)}`;
                    }
                  }}
                />
              ) : (
                <div className="w-44 h-44 bg-slate-100 flex items-center justify-center rounded text-slate-400 text-xs">
                  Memuat QRIS...
                </div>
              )}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="bg-white px-1.5 py-0.5 rounded shadow border border-slate-300 font-black text-rose-700 text-[8px]">
                  QRIS
                </div>
              </div>
            </div>

            {/* Expiry Info */}
            {qrisPaymentData?.qrisExpiredAt && (
              <p className="text-[9px] text-orange-600 font-semibold -mt-1 mb-1">
                ⏱ Berlaku s/d: {new Date(qrisPaymentData.qrisExpiredAt).toLocaleTimeString('id-ID')}
              </p>
            )}

            {/* No Transaksi */}
            {qrisPaymentData?.noTransaksi && (
              <p className="text-[9px] text-slate-400 font-mono mb-2">
                Ref: {qrisPaymentData.noTransaksi}
              </p>
            )}

            <p className="text-[10px] text-slate-500">
              Scan dengan aplikasi BCA, Mandiri, BRImo, BNI, Muamalat DIN, GoPay, Dana, OVO
            </p>

            <div className="mt-3 pt-3 flex gap-2">
              <button
                onClick={handleDownloadQR}
                className="flex-1 py-2.5 bg-sky-500 hover:bg-sky-600 text-white rounded-xl text-[11px] font-black shadow-sm transition flex items-center justify-center gap-1.5"
              >
                <Download className="w-4 h-4" />
                Download QRIS
              </button>
              <button
                onClick={() => { setShowQrModal(false); setQrisPaymentData(null); }}
                className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-[11px] font-bold transition"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Top Up Tabungan (Support E-Wallet, VA, QRIS) */}
      {showTopUpTabunganModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs">
          <div className="bg-white rounded-2xl max-w-sm w-full p-5 border border-slate-200 shadow-2xl animate-in fade-in space-y-4">
            <div className="flex justify-between items-center pb-2 border-b border-slate-100">
              <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                <Wallet className="w-4 h-4 text-emerald-600" />
                <span>Top Up Tabungan Siswa</span>
              </h3>
              <button
                onClick={() => setShowTopUpTabunganModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Pilih Nominal Top Up:</label>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  {[20000, 50000, 100000, 200000].map((nom) => (
                    <button
                      key={nom}
                      type="button"
                      onClick={() => setTopUpNominal(nom)}
                      className={`py-2 rounded-lg font-bold border transition ${
                        topUpNominal === nom
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      Rp {nom.toLocaleString('id-ID')}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  value={topUpNominal}
                  onChange={(e) => setTopUpNominal(Number(e.target.value))}
                  placeholder="Nominal kustom"
                  className="w-full px-3 py-2 border rounded-lg font-mono font-bold text-slate-800"
                />
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1.5">Metode Pembayaran:</label>
                <div className="space-y-2">
                  <label className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition ${
                    topUpSource === 'EWALLET' ? 'border-indigo-500 bg-indigo-50 text-indigo-950 font-semibold ring-1 ring-indigo-400' : 'border-slate-200 bg-slate-50 text-slate-700'
                  }`}>
                    <input
                      type="radio"
                      name="topUpSource"
                      checked={topUpSource === 'EWALLET'}
                      onChange={() => setTopUpSource('EWALLET')}
                      className="text-indigo-600"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-xs">📱 Saldo E-Wallet Siswa</span>
                        <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded font-bold">Bebas Biaya</span>
                      </div>
                      <p className="text-[10px] text-indigo-700 font-medium">
                        Saldo: Rp {(student.saldoEWallet || 0).toLocaleString('id-ID')} (Otomatis debit)
                      </p>
                    </div>
                  </label>

                  <label className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition ${
                    topUpSource === 'DUITKU_VA' ? 'border-amber-500 bg-amber-50 text-amber-950 font-semibold ring-1 ring-amber-400' : 'border-slate-200 bg-slate-50 text-slate-700'
                  }`}>
                    <input
                      type="radio"
                      name="topUpSource"
                      checked={topUpSource === 'DUITKU_VA'}
                      onChange={() => setTopUpSource('DUITKU_VA')}
                      className="text-amber-600"
                    />
                    <div className="flex-1">
                      <span className="font-bold text-xs block">Virtual Account Bank (BCA, BRI, Mandiri)</span>
                      <span className="text-[10px] text-slate-500">Dicek otomatis oleh gateway</span>
                    </div>
                  </label>

                  {topUpNominal <= 50000 && (
                    <label className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition ${
                      topUpSource === 'DUITKU_QRIS' ? 'border-sky-500 bg-sky-50 text-sky-950 font-semibold ring-1 ring-sky-400' : 'border-slate-200 bg-slate-50 text-slate-700'
                    }`}>
                      <input
                        type="radio"
                        name="topUpSource"
                        checked={topUpSource === 'DUITKU_QRIS'}
                        onChange={() => setTopUpSource('DUITKU_QRIS')}
                        className="text-sky-600"
                      />
                      <div className="flex-1">
                        <span className="font-bold text-xs block">QRIS Standar Nasional</span>
                        <span className="text-[10px] text-slate-500">Maks. Rp 50.000</span>
                      </div>
                    </label>
                  )}
                </div>
              </div>

              {topUpSource === 'EWALLET' && topUpNominal > (student.saldoEWallet || 0) && (
                <p className="text-[11px] text-rose-600 font-semibold p-2 bg-rose-50 border border-rose-200 rounded">
                  ⚠ Saldo E-Wallet tidak mencukupi (Rp {(student.saldoEWallet || 0).toLocaleString('id-ID')}). Silakan top up E-Wallet terlebih dahulu atau pilih metode lain.
                </p>
              )}

              <div className="pt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowTopUpTabunganModal(false)}
                  className="flex-1 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg"
                >
                  Batal
                </button>
                <button
                  type="button"
                  disabled={isToppingUp || (topUpSource === 'EWALLET' && topUpNominal > (student.saldoEWallet || 0))}
                  onClick={async () => {
                    try {
                      setIsToppingUp(true);
                      const res = await api.topUpTabungan({
                        siswaId: student.id,
                        nominal: topUpNominal,
                        channel: topUpSource,
                      });

                      if (res.success) {
                        setShowTopUpTabunganModal(false);
                        if (topUpSource === 'EWALLET') {
                          student.saldoEWallet = Math.max(0, (student.saldoEWallet || 0) - topUpNominal);
                          student.saldoTabungan = (student.saldoTabungan || 0) + topUpNominal;
                        }
                        showAlert(res.message || 'Top Up Tabungan berhasil diproses!', { type: 'success' });
                        if (onProcessPayment) {
                          await onProcessPayment({ siswaId: student.id, items: [], channel: 'EWALLET', reloadOnly: true });
                        }
                      } else {
                        showAlert(res.message || 'Gagal memproses top up tabungan', { type: 'danger' });
                      }
                    } catch (err: any) {
                      showAlert(`Gagal: ${err.message}`, { type: 'danger' });
                    } finally {
                      setIsToppingUp(false);
                    }
                  }}
                  className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold rounded-lg shadow-sm"
                >
                  {isToppingUp ? 'Memproses...' : 'Konfirmasi Top Up'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Vite HMR Force Trigger 1 */}
    </div>
  );
};
